/**
 * Safely format a number with fixed decimal places
 * Handles cases where the value might be a string or object from DynamoDB
 *
 * @param value Any value that should be formatted as a number
 * @param digits Number of decimal places (default: 1)
 * @returns Formatted string with fixed decimal places
 */
export const safeToFixed = (value: any, digits: number = 1): string => {
  if (value === undefined || value === null) return '0';

  // Convert to number if it's not already
  const num = typeof value === 'number' ? value : Number(value);

  // Check if it's a valid number
  return isNaN(num) ? '0' : num.toFixed(digits);
};

/**
 * Safely compare a numeric value against a threshold
 * Handles cases where the value might be a string or object from DynamoDB
 *
 * @param value Any value that should be treated as a number
 * @param threshold The threshold to compare against
 * @param operator The comparison operator ('>', '>=', '<', '<=', '==')
 * @returns Boolean result of the comparison, false if value is not a valid number
 */
export const safeNumberCompare = (
  value: any,
  threshold: number = 0,
  operator: '>' | '>=' | '<' | '<=' | '==' = '>=',
): boolean => {
  if (value === undefined || value === null) return false;

  // Convert to number if it's not already
  const num = typeof value === 'number' ? value : Number(value);

  // Check if it's a valid number
  if (isNaN(num)) return false;

  // Perform the comparison
  switch (operator) {
    case '>':
      return num > threshold;
    case '>=':
      return num >= threshold;
    case '<':
      return num < threshold;
    case '<=':
      return num <= threshold;
    case '==':
      return num === threshold;
    default:
      return false;
  }
};

/**
 * Safely format a number with locale-specific formatting
 * Handles cases where the value might be a string or object from DynamoDB
 *
 * @param value Any value that should be formatted as a number
 * @returns Locale-formatted string representation of the number
 */
export const safeToLocaleString = (value: any): string => {
  if (value === undefined || value === null) return '0';

  // Convert to number if it's not already
  const num = typeof value === 'number' ? value : Number(value);

  // Check if it's a valid number
  return isNaN(num) ? '0' : num.toLocaleString();
};

/**
 * Safely calculate a percentage from numerator and denominator
 * Handles cases where values might be strings or objects from DynamoDB
 *
 * @param numerator The top value in the division
 * @param denominator The bottom value in the division
 * @param digits Number of decimal places (default: 1)
 * @returns Formatted percentage string with % symbol
 */
export const safeCalculatePercentage = (numerator: any, denominator: any, digits: number = 1): string => {
  if (numerator === undefined || numerator === null || denominator === undefined || denominator === null) {
    return '0%';
  }

  // Convert to numbers if they're not already
  const num = typeof numerator === 'number' ? numerator : Number(numerator);
  const den = typeof denominator === 'number' ? denominator : Number(denominator);

  // Check if they're valid numbers and denominator is not zero
  if (isNaN(num) || isNaN(den) || den === 0) {
    return '0%';
  }

  // Calculate and format the percentage
  return `${((num / den) * 100).toFixed(digits)}%`;
};
